/**
 * Stone Investment Area Page Component
 * 
 * Complete area page layout with Stone Investment's luxury aesthetic:
 * - Cinematic hero
 * - Editorial content sections
 * - Property showcase
 * - Area navigation
 */

'use client';

import { useEffect, useState } from 'react';
import type { AreaPageProps, AreaWeatherData } from '@routeroyal/theme-core';
import { fetchAreaWeather } from '@routeroyal/theme-core';

import { StoneAreaHero } from './StoneAreaHero';
import { StoneAreaOverview } from './StoneAreaOverview';
import { StoneAreaStats } from './StoneAreaStats';
import { StoneAreaMap } from './StoneAreaMap';
import { StoneAreaProperties } from './StoneAreaProperties';
import { StoneAreaSelector } from './StoneAreaSelector';
import { StoneFAQ } from './StoneFAQ';

export interface StoneAreaPageProps extends AreaPageProps {
  /**
   * Show weather data (fetched client-side)
   * @default true
   */
  showWeather?: boolean;
  
  /**
   * Show property listings
   * @default true
   */
  showProperties?: boolean;
  
  /**
   * Show area selector
   * @default true
   */
  showAreaSelector?: boolean;
}

export function StoneAreaPage({
  data,
  regions,
  locale = 'en',
  messages,
  propertyBasePath = '/properties',
  areaBasePath = '/areas',
  showWeather = true,
  showProperties = true,
  showAreaSelector = true,
  className = '',
}: StoneAreaPageProps) {
  const [weather, setWeather] = useState<AreaWeatherData | null>(data.weather || null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  // Fetch weather data client-side
  useEffect(() => {
    if (!showWeather || data.weather) return;

    const loadWeather = async () => {
      setWeatherLoading(true);
      try {
        const weatherData = await fetchAreaWeather(data.latitude, data.longitude);
        if (weatherData) {
          setWeather(weatherData);
        }
      } catch (error) {
        console.error('Failed to fetch weather:', error);
      } finally {
        setWeatherLoading(false);
      }
    };

    loadWeather();
  }, [data.latitude, data.longitude, data.weather, showWeather]);

  // Generate view all properties link
  const viewAllLink = `/${locale}${propertyBasePath}?area=${data.slug}`;

  return (
    <div className={`font-sans text-stone-900 bg-stone-50 min-h-screen ${className}`}>
      {/* Hero Section */}
      <StoneAreaHero 
        area={{
          name: data.name,
          region: data.region,
          heroImage: data.heroImage,
          coordinates: data.coordinates,
          latitude: data.latitude,
          longitude: data.longitude,
        }} 
      />

      {/* Main Content Container */}
      <div className="container mx-auto max-w-7xl px-6 md:px-12 -mt-16 relative z-10 pb-20">
        {/* Title Card */}
        <div className="bg-white p-8 md:p-12 shadow-lg mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-3">
                {data.region}
              </p>
              <h1 
                className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-900 leading-[1.1]"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                {data.name}
              </h1>
            </div>

            {data.coordinates && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-sans mb-2">
                  {messages?.coordinates || 'Coordinates'}
                </p>
                <p className="font-serif text-xl text-stone-900 italic">
                  {data.coordinates.split(',')[0]?.trim()}
                </p>
                <p className="font-serif text-xl text-stone-700 italic">
                  {data.coordinates.split(',')[1]?.trim()}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Content Grid: Overview + Map */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
          {/* Overview Section */}
          <div className="lg:col-span-8">
            <StoneAreaOverview 
              area={{
                name: data.name,
                description: data.description,
                highlights: data.highlights,
              }}
              additionalContent={`${data.name} offers a unique blend of natural beauty and refined living, making it an ideal destination for those seeking exceptional properties.`}
            />
          </div>

          {/* Map Section */}
          <div className="lg:col-span-4">
          <StoneAreaMap 
            area={{
              name: data.name,
              latitude: data.latitude,
              longitude: data.longitude,
              coordinates: data.coordinates,
            }}
          />
          </div>
        </div>

        {/* Stats Section */}
        <StoneAreaStats 
          area={{
            name: data.name,
            ...(data.properties?.length !== undefined && { propertyCount: data.properties.length }),
          }}
          weather={weather}
          weatherLoading={weatherLoading}
          {...(data.marketStats !== undefined && { marketStats: data.marketStats })}
        />
      </div>

      {/* Properties Section */}
      {showProperties && data.properties && data.properties.length > 0 && (
        <StoneAreaProperties
          properties={data.properties}
          areaName={data.name}
          viewAllLink={viewAllLink}
          maxItems={3}
          locale={locale}
        />
      )}

      {data.faqs && data.faqs.length > 0 ? (
        <StoneFAQ
          faqs={data.faqs}
          locale={locale}
          className="bg-white"
          title={locale === 'fr' ? `À propos de ${data.name}` : `About ${data.name}`}
          description={
            locale === 'fr'
              ? `Questions fréquentes sur l'immobilier et le style de vie à ${data.name}.`
              : `Common questions about property and lifestyle in ${data.name}.`
          }
        />
      ) : null}

      {/* Area Selector */}
      {showAreaSelector && regions && regions.length > 0 && (
        <StoneAreaSelector
          regions={regions}
          currentArea={data.slug}
          basePath={areaBasePath}
          locale={locale}
        />
      )}
    </div>
  );
}

export default StoneAreaPage;
